import { inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CanActivateFn } from '@angular/router';
import { map, take } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { faro } from '@grafana/faro-web-sdk';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { REDIRECT_FLAG, SSO_TARGET_URL_KEY } from '../constants/auth.constants';

export const authGuard: CanActivateFn = (_route, state) => {
  // All injection up front: the guard is only in an injection context until it
  // returns, and two of these used to sit below a conditional early return.
  const doc = inject(DOCUMENT);
  const oidc = inject(OidcSecurityService);
  // The shell stays on the sign-in screen until this guard rules, so every exit
  // below has to report which of the four outcomes it took.
  const auth = inject(AuthService);

  const browserUrl = doc.location.href;
  // A code-flow callback is in progress when the URL still carries `?code=`.
  const hasAuthCode = doc.location.search.includes('code=');

  // TODO(SSO): remove debug logging once login flow is stable.
  console.log('[SSO][guard] activate', {
    targetUrl: state.url,
    browserUrl,
    hasAuthCode,
    pingAuthInit: environment.pingAuthInit,
  });

  // Local/mock mode: Ping redirect flow does not run on localhost. `refresh()`
  // has already reported `authenticated` for this case.
  if (!environment.pingAuthInit) {
    return true;
  }

  const storage = doc.defaultView?.sessionStorage ?? null;

  return oidc.isAuthenticated$.pipe(
    take(1),
    map(({ isAuthenticated }) => {
      const redirected = !!storage?.getItem(REDIRECT_FLAG);
      console.log('[SSO][guard] isAuthenticated$ emitted', {
        isAuthenticated,
        redirectAlreadyAttempted: redirected,
        hasAuthCode,
      });

      if (isAuthenticated) {
        console.log('[SSO][guard] decision: ALLOW');
        storage?.removeItem(REDIRECT_FLAG);
        // `refresh()` is already sending this tab to FILM (see
        // `handOffToFilm`). Releasing the shell now would flash this app
        // before the page unloads.
        if (auth.phase() === 'redirecting') {
          return true;
        }
        // A silent renew can authenticate after bootstrap, i.e. after
        // `refresh()` has already given up. Without this the route would
        // activate behind a shell still showing "Signing you in…".
        auth.reportDecision('authenticated');
        return true;
      }

      // A `?code=` is still in the URL -> checkAuth() is mid-exchange.
      // Calling authorize() now would navigate away and abort the token POST,
      // leaving codeFlowInProgress stuck. Do NOT redirect; let the exchange finish.
      if (hasAuthCode) {
        console.warn(
          '[SSO][guard] decision: WAIT (auth code present, exchange in progress — not redirecting)',
        );
        return false;
      }

      // Already redirected once and still not authenticated -> stop looping.
      if (redirected) {
        console.warn(
          '[SSO][guard] decision: BLOCK (already redirected, still not authenticated)',
        );
        faro?.api?.pushError(
          new Error('SSO login failed: token exchange did not authenticate the user.'),
        );
        // Nothing else will fire. Let the shell say so and offer a retry
        // instead of leaving a spinner running forever.
        auth.reportDecision('failed');
        return false;
      }

      console.log('[SSO][guard] decision: REDIRECT to Ping (authorize)', {
        stashedTargetUrl: state.url,
      });
      storage?.setItem(REDIRECT_FLAG, '1');
      // Preserve the deep-link target across the Ping round-trip. Skip '/' so a
      // user landing on root does not overwrite a target stashed by a prior nav.
      if (state.url && state.url !== '/') {
        storage?.setItem(SSO_TARGET_URL_KEY, state.url);
      }
      auth.reportDecision('redirecting');
      oidc.authorize();
      return false;
    }),
  );
};
